import { globalStyle, keyframes, type StyleRule, style } from '@vanilla-extract/css';

import {
  card,
  DESKTOP_PAGE,
  FOCUS_OUTLINE,
  pageContainer,
  pageSurface,
  SUPPORTS_COLOR_MIX,
  tokens,
  WIDE_PAGE,
} from './tipJarTheme.css';

// A style module may only export styles and plain values, so this one keeps its own copy of the theme's helper.
const mix = (color: string, percent: number, base: string) => `color-mix(in oklab, ${color} ${percent}%, ${base})`;

export const page = style([
  pageSurface,
  {
    containerName: pageContainer,
    containerType: 'inline-size',
    width: '100%',
    minHeight: '100vh',
    background: tokens.surface,
    color: tokens.ink,
    fontFamily: tokens.bodyFont,
    WebkitFontSmoothing: 'antialiased',
    // A breath of the page's own text over its background, so a card the same color as the page still stands off it.
    // On a page that already differs from its cards, this is too faint to change its color.
    '@supports': {
      [SUPPORTS_COLOR_MIX]: { background: mix(tokens.ink, 3, tokens.surface) },
    },
  },
]);

export const fullBleedBanner = style({
  display: 'block',
  width: '100%',
  height: '180px',
  objectFit: 'cover',
  background: tokens.track,
  '@container': {
    [WIDE_PAGE]: { height: '240px' },
    [DESKTOP_PAGE]: { height: 'clamp(280px, 22cqi, 360px)' },
  },
});

export const headerBand = style({
  height: '112px',
  background: tokens.tint,
  '@supports': {
    [SUPPORTS_COLOR_MIX]: {
      background: mix(tokens.primary, 14, tokens.surface),
    },
  },
  '@container': {
    [WIDE_PAGE]: { height: '160px' },
    [DESKTOP_PAGE]: { height: '200px' },
  },
});

export const bannerColumn = style({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  boxSizing: 'border-box',
  maxWidth: '632px',
  margin: '-32px auto 0',
  padding: '0 16px 64px',
  '@container': {
    [WIDE_PAGE]: { gap: '24px', maxWidth: '648px', marginTop: '-80px', padding: '0 24px 96px' },
    [DESKTOP_PAGE]: { marginTop: '-120px' },
  },
});

export const bannerCard = style([card, { gap: '24px' }]);

export const column = style({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  boxSizing: 'border-box',
  maxWidth: '632px',
  margin: '0 auto',
  padding: '32px 16px 64px',
  '@container': {
    [WIDE_PAGE]: { gap: '32px', maxWidth: '648px', padding: '64px 24px 96px' },
  },
});

export const split = style({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gridTemplateAreas: '"story" "aside"',
  gap: '24px',
  boxSizing: 'border-box',
  maxWidth: '1120px',
  margin: '0 auto',
  padding: '24px 16px 64px',
  '@container': {
    [WIDE_PAGE]: { gap: '32px', padding: '56px 24px 96px' },
    [DESKTOP_PAGE]: {
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 440px)',
      gridTemplateAreas: '"story aside"',
      alignItems: 'start',
      columnGap: '64px',
      padding: '64px 32px 96px',
    },
  },
});

export const splitStory = style({ gridArea: 'story' });

export const splitAside = style({
  gridArea: 'aside',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

export const STICKY_MARGIN_PX = 24;
const STICKY_TOP = `${STICKY_MARGIN_PX}px`;

// The jar and the share card stay in view together beside a long story, when the screen fits both.
export const splitAsideSticky = style({
  '@container': {
    [DESKTOP_PAGE]: { position: 'sticky', top: STICKY_TOP },
  },
});

// Otherwise the jar stays in view on its own: the column runs the story's length, and the jar rides down the part
// above the share card, which waits at the bottom.
export const splitAsideTrack = style({
  '@container': {
    [DESKTOP_PAGE]: { alignSelf: 'stretch' },
  },
});

export const splitJarTrack = style({
  '@container': {
    [DESKTOP_PAGE]: { flexGrow: 1 },
  },
});

export const splitJarSticky = style({
  '@container': {
    [DESKTOP_PAGE]: {
      '@media': {
        'screen and (min-height: 760px)': { position: 'sticky', top: STICKY_TOP },
      },
    },
  },
});

export const splitBanner = style({
  display: 'block',
  width: '100%',
  aspectRatio: '16 / 9',
  objectFit: 'cover',
  borderRadius: tokens.cardRadius,
  background: tokens.track,
  '@container': {
    [DESKTOP_PAGE]: { aspectRatio: '4 / 3' },
  },
});

const rise = keyframes({
  from: { opacity: 0, transform: 'translateY(8px)' },
});

// A CSS entrance plays from the server-rendered HTML, so the page is never blank while it hydrates.
for (const container of [column, bannerColumn, split]) {
  globalStyle(`${container} > *`, {
    animation: `${rise} 480ms cubic-bezier(0.16, 1, 0.3, 1) both`,
    '@media': {
      '(prefers-reduced-motion: reduce)': { animation: 'none' },
    },
  });

  for (let position = 1; position <= 3; position += 1) {
    globalStyle(`${container} > *:nth-child(${position})`, { animationDelay: `${(position - 1) * 60}ms` });
  }
}

export const intro = style({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const identity = style({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  margin: 0,
  fontSize: '15px',
  fontWeight: 600,
  lineHeight: '20px',
});

export const identityLink = style({
  alignSelf: 'flex-start',
  borderRadius: '8px',
  color: 'inherit',
  textDecoration: 'none',
  textUnderlineOffset: '3px',
  selectors: {
    '&:hover': { textDecoration: 'underline' },
    '&:focus-visible': FOCUS_OUTLINE,
  },
});

export const logo = style({
  flexShrink: 0,
  width: '36px',
  height: '36px',
  borderRadius: '999px',
  objectFit: 'cover',
  background: tokens.tint,
  boxShadow: `0 0 0 1px ${tokens.line}`,
});

export const banner = style({
  display: 'block',
  width: '100%',
  aspectRatio: '12 / 5',
  objectFit: 'cover',
  borderRadius: tokens.cardRadius,
  background: tokens.track,
});

export const heading = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '10px',
});

export const title = style({
  margin: 0,
  fontFamily: tokens.headerFont,
  fontSize: '30px',
  fontWeight: 700,
  lineHeight: 1.15,
  letterSpacing: '-0.02em',
  textWrap: 'balance',
  overflowWrap: 'break-word',
  '@container': {
    [WIDE_PAGE]: { fontSize: '38px' },
  },
});

export const description = style({
  alignSelf: 'stretch',
  color: tokens.muted,
});

// The rich text sets a tight line height for headings and buttons; a description reads better with room.
globalStyle(`${description} .rich-text-element :is(p, li)`, {
  lineHeight: 1.6,
});

// About four lines of the 16 to 17px description. `clip` rather than `hidden`, so focusing a link past the fold
// can't scroll the text inside its frame.
const folded: StyleRule = {
  maxHeight: '7em',
  overflow: ['hidden', 'clip'],
  fontSize: '17px',
};

export const descriptionFolded = style(folded);

export const descriptionFoldedWhenStacked = style({
  '@container': {
    [`${pageContainer} (max-width: 899.98px)`]: folded,
  },
});

export const descriptionFaded = style({
  maskImage: 'linear-gradient(180deg, #000 55%, transparent)',
  WebkitMaskImage: 'linear-gradient(180deg, #000 55%, transparent)',
});

export const readMoreIcon = style({
  transition: 'transform 200ms ease',
  selectors: {
    '[aria-expanded="true"] > &': { transform: 'rotate(180deg)' },
  },
  '@media': {
    '(prefers-reduced-motion: reduce)': { transition: 'none' },
  },
});

export const jar = style({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
});

export const jarUnderStory = style({
  paddingTop: '24px',
  borderTop: `1px solid ${tokens.line}`,
});

export const jarName = style({
  margin: 0,
  fontSize: '15px',
  fontWeight: 600,
  lineHeight: '20px',
});

export const supporters = style({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  margin: 0,
  fontSize: '15px',
  fontWeight: 500,
  lineHeight: '20px',
});

export const supportersIcon = style({
  display: 'flex',
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  borderRadius: '999px',
  background: tokens.track,
});

// Focus moves here when the stage changes, so screen readers follow the reader into the next one.
export const stage = style({
  outline: 'none',
});

export const checkout = style({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '16px',
});

// Stripe's embedded checkout is white whatever the site, so its frame is too.
export const checkoutFrame = style({
  width: '100%',
  overflow: 'hidden',
  border: `1px solid ${tokens.line}`,
  borderRadius: tokens.fieldRadius,
  background: '#FFFFFF',
});

export const error = style({
  margin: 0,
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '20px',
  color: tokens.danger,
});

export const checkoutStatus = style([error, { color: tokens.muted }]);

export const secureCheckout = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  margin: 0,
  fontSize: '12px',
  lineHeight: '16px',
  color: tokens.muted,
});
